import { useCallback, useMemo } from 'react';
import { Match, Player } from '../types';

interface UseMatchUtilitiesProps {
  match: Match | null;
  user: any;
  onShowNotification: (message: string, type: 'success' | 'error') => void;
  onFetchMatch: () => Promise<void>;
  onSetShowImportListModal: (show: boolean) => void;
  onOpenJoinModal: (asSubstitute?: boolean, teamIndex?: number, positionIndex?: number) => void;
}

interface UseMatchUtilitiesReturn {
  isGuest: boolean;
  clearGuestJoinState: (matchId: string) => void;
  handleLoginClick: (matchId: string) => void;
  handleImportList: () => void;
  handleImportSuccess: () => void;
  handleJoinPosition: (teamIndex: number, positionIndex: number, userIsCreator: boolean) => any;
}

export const useMatchUtilities = ({
  match,
  user,
  onShowNotification,
  onFetchMatch,
  onSetShowImportListModal,
  onOpenJoinModal
}: UseMatchUtilitiesProps): UseMatchUtilitiesReturn => {
  // Detectar si es invitado
  const isGuest = useMemo(() => !user, [user]);

  // Función para limpiar el estado del invitado cuando abandone el partido
  const clearGuestJoinState = useCallback((matchId: string) => {
    localStorage.removeItem(`guest_joined_${matchId}`);
  }, []);

  // Definir handleLoginClick antes de usarlo en JoinModal
  const handleLoginClick = useCallback((matchId: string) => {
    // Guardar la URL actual para redirigir después del login
    localStorage.setItem('redirectAfterAuth', window.location.pathname);
    localStorage.setItem('joinMatchAfterLogin', JSON.stringify({ matchId }));
    // Aquí necesitarías acceso a loginWithGoogle del contexto de auth
    // loginWithGoogle();
  }, []);

  // Función para manejar la importación de lista
  const handleImportList = useCallback(() => {
    // Abrir el modal de importación de lista
    onSetShowImportListModal(true);
  }, [onSetShowImportListModal]);

  // Función para manejar el éxito de la importación
  const handleImportSuccess = useCallback(() => {
    onFetchMatch();
    onShowNotification('Lista importada exitosamente', 'success');
  }, [onFetchMatch, onShowNotification]);

  // Función para manejar clicks en posiciones
  const handleJoinPosition = useCallback((teamIndex: number, positionIndex: number, userIsCreator: boolean) => {
    if (!match) return;
    
    // Solo el organizador puede agregar jugadores
    if (!userIsCreator) {
      onShowNotification('Solo el organizador puede agregar jugadores', 'error');
      return;
    }
    
    // Calcular el máximo total de jugadores permitidos
    const maxTotalPlayers = match.maxTotalPlayers || match.maxPlayers * 2;
    
    // Contar jugadores principales actuales
    const currentMainPlayers = match.players.length;
    
    // Verificar si hay espacio para más jugadores principales
    if (currentMainPlayers < maxTotalPlayers) {
      onOpenJoinModal(false, teamIndex, positionIndex);
    } else {
      // Si está lleno, preguntar si quiere unirse como suplente
      if (window.confirm('El partido está completo para jugadores principales. ¿Te gustaría unirte como suplente?')) {
        onOpenJoinModal(true);
      }
    }
  }, [match, onShowNotification, onOpenJoinModal]);

  return {
    isGuest,
    clearGuestJoinState,
    handleLoginClick,
    handleImportList,
    handleImportSuccess,
    handleJoinPosition
  };
};
